'use client';

import { useQuery } from '@tanstack/react-query';
import { getAccreditationEligibility } from 'services/secondary';

type EligibilityStatus =
  | 'ACCREDITED_PERSON'
  | 'ELIGIBLE'
  | 'ACCREDITED_ACCOUNT'
  | 'NOT_ELIGIBLE'
  | null;

interface AccreditationEligibilityResponse {
  eligibility?: EligibilityStatus;
  currentAccreditationStatus?: string;
}

const ACCREDITED_STATUSES = new Set<EligibilityStatus>([
  'ACCREDITED_PERSON',
  'ELIGIBLE',
  'ACCREDITED_ACCOUNT',
]);

const ACCREDITED_BY_STATUS = new Set(['QUALIFIED_PURCHASER_VERIFIED', 'ACCREDITED_VERIFIED']);

const parseEligibilityStatus = (
  data?: AccreditationEligibilityResponse | null,
): EligibilityStatus => {
  if (!data) return null;
  return data.eligibility ?? null;
};

interface UseGetAccreditationEligibilityOptions {
  primaryUserId?: string;
  enabled?: boolean;
}

const useGetAccreditationEligibility = ({
  primaryUserId,
  enabled = true,
}: UseGetAccreditationEligibilityOptions) => {
  const { data, isLoading, isFetching, error } = useQuery<AccreditationEligibilityResponse>({
    queryKey: ['accreditationEligibility', primaryUserId],
    queryFn: () => getAccreditationEligibility({ primaryUserId }),
    enabled: !!primaryUserId && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: 2,
  });

  const eligibilityStatus = parseEligibilityStatus(data);
  const isQualifiedPurchaser = data?.currentAccreditationStatus === 'QUALIFIED_PURCHASER_VERIFIED';
  const isAccreditedByCurrentStatus =
    !!data?.currentAccreditationStatus && ACCREDITED_BY_STATUS.has(data.currentAccreditationStatus);
  const isAccredited =
    (eligibilityStatus !== null && ACCREDITED_STATUSES.has(eligibilityStatus)) ||
    isAccreditedByCurrentStatus;

  return {
    isAccredited,
    isQualifiedPurchaser,
    eligibilityStatus,
    isLoading,
    isFetching,
    error,
    data,
  };
};

export default useGetAccreditationEligibility;
